import React, { useState } from "react";
import { Menu, Play, Square, Keyboard, X, Share2 } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import StatusPill from "./StatusPill";
import ProjectMenu from "./ProjectMenu";
import ThemeSelector from "./ThemeSelector";

function shortcutLabel() {
  if (typeof navigator !== "undefined" && /Mac/i.test(navigator.platform)) {
    return "\u2318 Enter";
  }
  return "Ctrl+Enter";
}

const SHORTCUTS = [
  { keys: ["⌘/Ctrl", "Enter"], desc: "Run code" },
  { keys: ["⌘/Ctrl", "."], desc: "Stop execution" },
  { keys: ["⌘/Ctrl", "`"], desc: "Toggle terminal" },
  { keys: ["⌘/Ctrl", "S"], desc: "Save project" },
  { keys: ["⌘/Ctrl", "Z"], desc: "Undo" },
  { keys: ["⌘/Ctrl", "Shift", "Z"], desc: "Redo" },
  { keys: ["⌘/Ctrl", "/"], desc: "Toggle comment" },
  { keys: ["⌘/Ctrl", "D"], desc: "Duplicate line" },
  { keys: ["Alt", "↑/↓"], desc: "Move line up/down" },
  { keys: ["⌘/Ctrl", "Shift", "K"], desc: "Delete line" },
  { keys: ["⌘/Ctrl", "Shift", "P"], desc: "Command palette" },
  { keys: ["Ctrl", "`"], desc: "Focus terminal" },
];

function ShortcutsModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-ide-panel border border-ide-borderHi rounded-xl shadow-glow w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-ide-border">
          <div className="flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-ide-accent" />
            <span className="text-sm font-semibold text-ide-text">Keyboard Shortcuts</span>
          </div>
          <button onClick={onClose} className="text-ide-mute hover:text-ide-text transition-colors p-1 rounded hover:bg-ide-surface">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-1 gap-1 max-h-80 overflow-auto">
          {SHORTCUTS.map(({ keys, desc }) => (
            <div key={desc} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-ide-surface transition-colors">
              <span className="text-sm text-ide-mute">{desc}</span>
              <div className="flex items-center gap-1">
                {keys.map((k, i) => (
                  <React.Fragment key={i}>
                    <kbd className="px-1.5 py-0.5 text-[10px] font-mono rounded border border-ide-borderHi bg-ide-surface text-ide-text">
                      {k}
                    </kbd>
                    {i < keys.length - 1 && <span className="text-ide-mute text-[10px]">+</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TopBar({
  languages,
  language,
  onLanguageChange,
  loading,
  isRunning,
  onRun,
  onStop,
  status,
  exitCode,
  execTimeMs,
  projectName,
  isProjectMode,
  saveState,
  isDirty,
  onProjectNew,
  onProjectOpen,
  onProjectSave,
  onProjectSaveAs,
  onProjectDelete,
  onShare,
  themeId,
  onThemeChange,
  compact = false,
  onMobileMenu,
}) {
  const runShortcut = shortcutLabel();
  const [showShortcuts, setShowShortcuts] = useState(false);

  return (
    <>
      <header
        className="flex items-center gap-1.5 sm:gap-3 px-2 sm:px-3 py-2 border-b border-ide-border bg-ide-panel2/98 backdrop-blur-sm select-none flex-none w-full min-w-0"
        style={{
          paddingTop: "max(0.5rem, env(safe-area-inset-top, 0px))",
          zIndex: 40,
          boxShadow: "0 1px 0 rgba(124,58,237,0.15), 0 4px 16px rgba(0,0,0,0.4)",
        }}
      >
        {compact && (
          <button
            onClick={onMobileMenu}
            className="flex-none text-ide-mute hover:text-ide-text rounded-md min-h-[36px] min-w-[36px] flex items-center justify-center hover:bg-ide-surface transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        {/* Brand */}
        <div className="flex items-center gap-2 flex-none">
          <img
            src="/logo.png"
            alt="NoEffortsAi iDE"
            className="h-7 w-7 rounded-md flex-none select-none"
            draggable={false}
          />
          <div className="hidden sm:flex items-baseline gap-1.5 whitespace-nowrap">
            <span className="text-[15px] font-bold tracking-tight"
              style={{ background: "linear-gradient(135deg,#e4eaff 0%,#c4b5fd 40%,#22d3ee 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              NoEffortsAi
            </span>
            <span className="text-[13px] font-medium text-ide-mute">iDE</span>
          </div>
        </div>

        {/* Project menu — desktop only */}
        {!compact && (
          <div className="min-w-0 flex-shrink">
            <ProjectMenu
              projectName={projectName}
              isProjectMode={isProjectMode}
              saveState={saveState}
              isDirty={isDirty}
              onNew={onProjectNew}
              onOpen={onProjectOpen}
              onSave={onProjectSave}
              onSaveAs={onProjectSaveAs}
              onDelete={onProjectDelete}
            />
          </div>
        )}

        <div className="min-w-0 flex-shrink">
          <LanguageSwitcher
            languages={languages}
            current={language}
            onChange={onLanguageChange}
            loading={loading}
            compact={compact}
          />
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto flex-none">
          {/* Theme selector — desktop only */}
          {!compact && onThemeChange && (
            <ThemeSelector themeId={themeId} onChange={onThemeChange} />
          )}

          {/* Share button */}
          <button
            className="flex items-center justify-center gap-1 px-2 py-1 min-h-[32px] rounded-md border border-ide-border bg-ide-surface hover:bg-ide-panel text-ide-text text-xs transition-colors"
            onClick={onShare}
            title="Share code (generate link)"
            data-testid="share-btn"
          >
            <Share2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Share</span>
          </button>

          {/* Keyboard shortcuts hint */}
          <button
            className="hidden sm:flex items-center justify-center text-ide-mute hover:text-ide-text rounded-md min-h-[32px] min-w-[32px] hover:bg-ide-surface transition-colors"
            onClick={() => setShowShortcuts(true)}
            title="Keyboard shortcuts"
            data-testid="shortcuts-btn"
          >
            <Keyboard className="h-4 w-4" />
          </button>

          <button
            className="ide-button-primary min-h-[36px] flex-none"
            onClick={onRun}
            disabled={isRunning}
            title={`Run (${runShortcut})`}
            aria-label="Run"
            data-testid="run-btn"
          >
            {isRunning ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3.5 w-3.5 inline-block rounded-full border-2 border-white/40 border-t-white animate-spin" />
                <span className="hidden sm:inline">Running</span>
              </span>
            ) : (
              <>
                <Play className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Run</span>
                <kbd className="hidden md:inline ml-1 text-[10px] font-mono opacity-60 px-1 py-0.5 rounded border border-white/20">
                  {runShortcut}
                </kbd>
              </>
            )}
          </button>

          <button
            className="ide-button-danger min-h-[36px] flex-none"
            onClick={onStop}
            disabled={!isRunning}
            title="Stop (\u2318/Ctrl+.)"
            aria-label="Stop"
            data-testid="stop-btn"
          >
            <Square className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Stop</span>
          </button>

          <div className="hidden sm:flex flex-none">
            <StatusPill status={status} execTimeMs={execTimeMs} exitCode={exitCode} />
          </div>
        </div>
      </header>

      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
    </>
  );
}
